import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import SearchableSelect from './SearchableSelect';
import { formatCategoriesForDropdown } from '../utils/categoryHelper';

const copyToClipboard = async (text) => {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch (err) {
        console.error('Failed to copy!', err);
        return false;
    }
};

const AddMemberModal = ({ isOpen, onClose, onSuccess }) => {
    const { user } = useAuth();
    const isChapterAdmin = user?.role === 'chapter_admin';
    const chapterLabel = user?.namingConvention?.chapterLabel || 'Chapter';
    const categoryLabel = user?.namingConvention?.categoryLabel || 'Category';
    const planLabel = user?.namingConvention?.planLabel || 'Plan';
    const [loadingData, setLoadingData] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const [chapters, setChapters] = useState([]);
    const [plans, setPlans] = useState([]);
    const [categories, setCategories] = useState([]);
    const [members, setMembers] = useState([]);

    const getAvailableCategories = () => {
        if (!formData.chapter) return categories;
        const takenCategoryIds = members
            .filter(m => m.chapter === formData.chapter && ['approved', 'active_pending'].includes(m.status))
            .map(m => m.category_id)
            .filter(id => id !== null && id !== undefined);
        return categories.filter(c => !takenCategoryIds.includes(c.id));
    };

    const [showCredentials, setShowCredentials] = useState(false);
    const [generatedCredentials, setGeneratedCredentials] = useState({ email: '', password: '' });

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        mobile: '',
        yearsInBusiness: '',
        chapter: '',
        categoryId: '',
        planId: '',
        referredById: '',
        referredByOther: ''
    });

    useEffect(() => {
        if (isOpen) {
            fetchMasterData();
            setFormData({
                name: '', email: '', mobile: '', yearsInBusiness: '',
                chapter: isChapterAdmin ? user?.chapter || '' : '',
                categoryId: '', planId: '', referredById: '', referredByOther: ''
            });
            setShowCredentials(false);
        }
    }, [isOpen, isChapterAdmin, user?.chapter]);

    const fetchMasterData = async () => {
        setLoadingData(true);
        try {
            const [chaptersRes, plansRes, categoriesRes, membersRes] = await Promise.all([
                api.get('/chapters'),
                api.get('/master/plans'),
                api.get('/master/categories'),
                api.get('/users')
            ]);
            setChapters(chaptersRes.data.data || []);
            setPlans(plansRes.data.data || []);
            setCategories(categoriesRes.data.data || []);
            setMembers(membersRes.data.data?.filter(m => ['approved', 'active_pending'].includes(m.status)) || []);
        } catch (err) {
            console.error('Failed to fetch master data:', err);
            toast.error('Failed to load required data');
        } finally {
            setLoadingData(false);
        }
    };

    const handleSubmit = async () => {
        if (!formData.name || !formData.email || !formData.chapter || !formData.categoryId || !formData.planId) {
            toast.error('Please fill in all required fields (Name, Email, Chapter, Category, Plan)');
            return;
        }

        // Mobile number validation
        const rawPhone = (formData.mobile || '').replace(/[\s\-+]/g, '').replace(/^91/, '');
        if (!/^\d{10}$/.test(rawPhone)) {
            toast.error('Please enter a valid 10-digit mobile number.');
            return;
        }

        setSubmitting(true);
        try {
            // Step 1: Register user to create a pending record
            const registerData = {
                organizationName: user?.organizationName || user?.subDomain || '',
                subDomain: user?.subDomain || '',
                name: formData.name,
                email: formData.email,
                contactNumber: formData.mobile,
                yearsInBusiness: formData.yearsInBusiness,
                silent: true
            };

            const regRes = await api.post('/auth/register-user', registerData);
            const newUserId = regRes.data.data.userId;

            // Step 2: Approve the user to assign membership details
            const approveRes = await api.put(`/users/${newUserId}/approve`, {
                name: formData.name,
                mobile: formData.mobile,
                chapter: formData.chapter,
                categoryId: formData.categoryId,
                planId: formData.planId,
                referredById: formData.referredById,
                referredByOther: formData.referredByOther
            });

            if (approveRes.data.credentials) {
                setGeneratedCredentials({
                    email: approveRes.data.credentials.email || formData.email,
                    password: approveRes.data.credentials.password || ''
                });
                setShowCredentials(true);
                toast.success('Member added and approved successfully!');
                if (onSuccess) onSuccess();
                // We do not close the modal immediately so they can copy credentials
                setTimeout(() => {
                    setShowCredentials(false);
                }, 30000);
            } else {
                toast.success('Member added and approved successfully!');
                if (onSuccess) onSuccess();
                onClose();
            }
        } catch (error) {
            console.error('Failed to add member:', error);
            toast.error(error.response?.data?.message || 'Failed to add member');
        } finally {
            setSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[9999] overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                    <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-sm" onClick={!showCredentials ? onClose : undefined}></div>
                </div>
                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                <div className="inline-block align-bottom bg-white rounded-2xl text-left shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full max-h-[90vh] overflow-y-auto">
                    {showCredentials ? (
                        <div className="p-6 bg-gradient-to-br from-blue-50 to-white border-2 border-blue-200">
                            <div className="flex items-start justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-black text-slate-900">Member Approved Successfully!</h3>
                                        <p className="text-sm text-slate-600 font-medium">Use these credentials to log in to the application</p>
                                    </div>
                                </div>
                                <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="bg-white rounded-xl p-4 border border-blue-100">
                                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-widest mb-2">Email ID</label>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="text"
                                            value={generatedCredentials.email}
                                            readOnly
                                            className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-semibold text-slate-900 outline-none"
                                        />
                                        <button
                                            onClick={async () => {
                                                const success = await copyToClipboard(generatedCredentials.email);
                                                if (success) toast.success('Email copied to clipboard');
                                            }}
                                            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-xs font-bold hover:bg-blue-700 transition-all active:scale-95"
                                        >
                                            Copy
                                        </button>
                                    </div>
                                </div>

                                <div className="bg-white rounded-xl p-4 border border-blue-100">
                                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-widest mb-2">Password</label>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="text"
                                            value={generatedCredentials.password}
                                            readOnly
                                            className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-semibold text-slate-900 outline-none"
                                        />
                                        <button
                                            onClick={async () => {
                                                const success = await copyToClipboard(generatedCredentials.password);
                                                if (success) toast.success('Password copied to clipboard');
                                            }}
                                            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-xs font-bold hover:bg-blue-700 transition-all active:scale-95"
                                        >
                                            Copy
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-6 flex justify-end">
                                <button
                                    onClick={onClose}
                                    className="px-6 py-2 bg-slate-900 text-white rounded-xl text-sm font-bold hover:bg-slate-800 transition-all shadow-lg shadow-slate-200"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-white px-4 pt-5 pb-4 sm:p-8 overflow-visible">                            <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-extrabold text-gray-900">Add New Member</h3>
                            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                            {loadingData ? (
                                <div className="flex justify-center items-center py-12">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {/* Member Info Section */}
                                    <div className="bg-gray-50 p-5 rounded-xl">
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-4">
                                            <div>
                                                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Full Name *</label>
                                                <input
                                                    type="text"
                                                    value={formData.name}
                                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                                    placeholder="Enter full name"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Email Address *</label>
                                                <input
                                                    type="email"
                                                    value={formData.email}
                                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                                    placeholder="Enter email"
                                                />
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Mobile</label>
                                                    <input
                                                        type="text"
                                                        value={formData.mobile}
                                                        onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                                        placeholder="Mobile"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Experience (Yrs)</label>
                                                    <input
                                                        type="number"
                                                        inputMode="numeric"
                                                        value={formData.yearsInBusiness}
                                                        onChange={(e) => setFormData({ ...formData, yearsInBusiness: e.target.value })}
                                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                                        placeholder="e.g. 5"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Assigned Details Section */}
                                    <div className="space-y-4 pt-2">
                                        <div className="flex items-center gap-4 mb-2">
                                            <h4 className="text-sm font-bold text-gray-900 whitespace-nowrap">Assign Membership Details</h4>
                                            <div className="h-px bg-gray-100 w-full" />
                                        </div>
                                        <div className="space-y-4">

                                            {/* Row 1 */}
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">
                                                        {chapterLabel} *
                                                    </label>

                                                    {isChapterAdmin ? (
                                                        <div className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm font-semibold text-gray-700 cursor-not-allowed">
                                                            {user?.chapter || 'Not Assigned'}
                                                        </div>
                                                    ) : (
                                                        <SearchableSelect
                                                            options={chapters.map(c => ({
                                                                value: c.name,
                                                                label: c.name
                                                            }))}
                                                            value={formData.chapter}
                                                            onChange={(val) =>
                                                                setFormData({ ...formData, chapter: val })
                                                            }
                                                            placeholder={`Search ${chapterLabel}`}
                                                        />
                                                    )}
                                                </div>

                                                <div>
                                                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">
                                                        {categoryLabel} *
                                                    </label>

                                                    <SearchableSelect
                                                        options={formatCategoriesForDropdown(getAvailableCategories()).map(c => ({
                                                            value: c.id,
                                                            label: c.displayName
                                                        }))}
                                                        value={formData.categoryId}
                                                        onChange={(val) =>
                                                            setFormData({ ...formData, categoryId: val })
                                                        }
                                                        placeholder={`Search ${categoryLabel}`}
                                                    />
                                                </div>
                                            </div>

                                            {/* Row 2 */}
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">
                                                        {planLabel} *
                                                    </label>

                                                    <SearchableSelect
                                                        options={plans.map(p => ({
                                                            value: p.id,
                                                            label: p.name
                                                        }))}
                                                        value={formData.planId}
                                                        onChange={(val) =>
                                                            setFormData({ ...formData, planId: val })
                                                        }
                                                        placeholder={`Search ${planLabel}`}
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">
                                                        Referral Member
                                                    </label>

                                                    <SearchableSelect
                                                        options={[
                                                            { value: 'other', label: 'Other' },
                                                            ...members.map(m => ({
                                                                value: m.id,
                                                                label: `${m.name}`
                                                            }))
                                                        ]}
                                                        value={formData.referredById}
                                                        onChange={(val) =>
                                                            setFormData({
                                                                ...formData,
                                                                referredById: val
                                                            })
                                                        }
                                                        placeholder="Search Referral Member"
                                                    />
                                                </div>
                                            </div>

                                            {/* Row 3 */}
                                            {formData.referredById === 'other' && (
                                                <div>
                                                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">
                                                        Referral Name
                                                    </label>

                                                    <input
                                                        type="text"
                                                        value={formData.referredByOther}
                                                        onChange={(e) =>
                                                            setFormData({
                                                                ...formData,
                                                                referredByOther: e.target.value
                                                            })
                                                        }
                                                        placeholder="Enter referral name"
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="mt-8 flex justify-end gap-3 border-t pt-6">
                                        <button
                                            onClick={onClose}
                                            className="px-6 py-2 rounded-xl border border-gray-300 text-sm font-bold text-gray-600 hover:bg-gray-50 outline-none"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleSubmit}
                                            disabled={submitting}
                                            className="px-8 py-2 bg-blue-600 text-white rounded-xl text-sm font-extrabold hover:bg-blue-700 transition-all shadow-lg shadow-blue-200 disabled:opacity-50 outline-none"
                                        >
                                            {submitting ? 'Adding...' : 'Add & Approve'}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AddMemberModal;
